// src/features/live-board/components/BoardRowColumn.tsx
//
// A row-list board column (`board.at_device`, `board.needs_help`): the only
// two `board.*` ids that resolve to a per-journey row list server-side
// (`V2BoardFinItMetricDefinitions.BoardAtDevice`/`BoardNeedsHelp`). Neither
// row carries a guest name, masked or otherwise — `journey_id`/`surface`/
// `device_id`/`step_kind`/`elapsed_seconds` (`at_device`) or `journey_id`/
// `surface`/`device_id`/`signal`/`component` (`needs_help`) are the only
// keys either dictionary ever sets (see the unit's own evidence file,
// "Design choices" — CF S6's "masked names" is not buildable from this
// contract as it stands). Rows are identified by device + step instead.
import React from 'react';
import { Box, Chip, List, ListItem, Skeleton, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { WidgetCard } from '@/components/common/WidgetCard';
import { MetricStamp } from './MetricStamp';
import type { BoardRow, MetricResultDto } from '@/types/analyticsV2';

interface BoardRowColumnProps {
  title: string;
  icon?: React.ReactNode;
  metric: MetricResultDto | undefined;
  loading: boolean;
  emptyLabel: string;
  renderRow: (row: BoardRow, key: string) => React.ReactNode;
  rowKey: (row: BoardRow, index: number) => string;
  /** Highlighted rows get a warning-tinted background — the hotel's own board.at_device highlight threshold (CF S6, D35). */
  highlightedKeys?: Set<string>;
  badgeCount?: number;
}

export const BoardRowColumn: React.FC<BoardRowColumnProps> = ({
  title,
  icon,
  metric,
  loading,
  emptyLabel,
  renderRow,
  rowKey,
  highlightedKeys,
  badgeCount,
}) => {
  const translate = useTranslate();
  const rows = metric && !metric.absent && Array.isArray(metric.value) ? (metric.value as BoardRow[]) : [];

  return (
    <WidgetCard
      title={title}
      icon={icon}
      action={
        badgeCount ? (
          <Chip size="small" color="warning" label={badgeCount} sx={{ fontWeight: 700 }} />
        ) : undefined
      }
    >
      {loading ? (
        <Skeleton height={80} />
      ) : (
        <>
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {emptyLabel}
            </Typography>
          ) : (
            <List dense disablePadding>
              {rows.map((row, i) => {
                const key = rowKey(row, i);
                const highlighted = highlightedKeys?.has(key);
                return (
                  <ListItem
                    key={key}
                    disableGutters
                    sx={{
                      py: 0.75,
                      px: 1,
                      mb: 0.5,
                      borderRadius: 1,
                      bgcolor: highlighted ? 'warning.light' : 'action.hover',
                    }}
                  >
                    {renderRow(row, key)}
                  </ListItem>
                );
              })}
            </List>
          )}
          {metric && <MetricStamp stamp={metric.stamp} />}
          {!metric && (
            <Typography variant="caption" color="text.secondary">
              {translate('resources.liveBoard.stamp.noData')}
            </Typography>
          )}
        </>
      )}
    </WidgetCard>
  );
};

export default BoardRowColumn;
